<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-table-filter :filter-title="$t('expensa.menu_reports')">
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.status"
              type="select"
              :label="$t('general.status')"
              :options="statusOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item v-if="showEmployeeFilter">
            <employee-autocomplete
              v-model="filters.employee"
              :rules="rules.employee"
              :params="{includeEmployees: 'currentAndPast'}"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.year"
              type="select"
              :label="$t('expensa.filter_year')"
              :options="yearOptions"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.month"
              type="select"
              :label="$t('expensa.filter_month')"
              :options="monthOptions"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.q"
              :label="$t('expensa.filter_text')"
              :rules="rules.q"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          @click="onClickReset"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <oxd-button
        :label="$t('expensa.new_report')"
        icon-name="plus"
        display-type="secondary"
        @click="onClickNew"
      />
    </div>
    <table-header :total="total" :loading="isLoading" />
    <oxd-text
      v-if="state === 'error'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.list_error') }}
    </oxd-text>
    <oxd-text
      v-else-if="state === 'empty'"
      tag="p"
      class="orangehrm-container expensa-empty"
    >
      {{ $t('expensa.no_records') }}
    </oxd-text>
    <div v-else class="orangehrm-container">
      <oxd-card-table
        :items="items.data || []"
        :headers="headers"
        :selectable="false"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
  </div>

  <oxd-dialog
    v-if="showNew"
    class="orangehrm-dialog-modal"
    @update:show="onCancelNew"
  >
    <div class="orangehrm-modal-header">
      <oxd-text type="card-title">{{ $t('expensa.new_report') }}</oxd-text>
    </div>
    <oxd-divider />
    <oxd-form :loading="saving" @submit-valid="onSaveNew">
      <oxd-form-row v-if="me && me.isFinance">
        <employee-autocomplete
          v-model="draft.employee"
          :rules="rules.employee"
          :params="{includeEmployees: 'onlyCurrent'}"
        />
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="draft.title"
          :label="$t('expensa.report_title')"
          :rules="rules.title"
          required
        />
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          v-model="draft.description"
          type="textarea"
          :label="$t('expensa.report_description')"
          :rules="rules.description"
        />
      </oxd-form-row>
      <oxd-divider />
      <oxd-form-actions>
        <required-text />
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancelNew"
        />
        <oxd-button
          type="submit"
          display-type="secondary"
          :label="$t('expensa.create')"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-dialog>
</template>

<script>
import {ref, computed, markRaw} from 'vue';
import {OxdDialog} from '@ohrm/oxd';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import usei18n from '@/core/util/composable/usei18n';
import {
  required,
  shouldNotExceedCharLength,
  validSelection,
} from '@/core/util/validation/rules';
import StatusPill from '@/orangehrmExpensaPlugin/components/StatusPill.vue';
import {expensaApi, errorMessage, pageUrl} from '../util/api';
import {eur, instant} from '../util/format';
import {REPORT_STATUSES, reportStatusKey} from '../util/labels';
import {listState} from '../util/listState';

const defaultFilters = {
  status: null,
  employee: null,
  year: null,
  month: null,
  q: '',
};

export default {
  name: 'ExpensaReports',
  components: {
    'oxd-dialog': OxdDialog,
    'employee-autocomplete': EmployeeAutocomplete,
  },
  setup() {
    const {$t} = usei18n();
    const {$tEmpName} = useEmployeeNameTranslate();
    const filters = ref({...defaultFilters});
    const me = ref(null);

    const serializedFilters = computed(() => ({
      status: filters.value.status?.id ?? null,
      empNumber: filters.value.employee?.id ?? null,
      fiscalYear: filters.value.year?.id ?? null,
      month: filters.value.month?.id ?? null,
      q: filters.value.q ? filters.value.q.trim() || null : null,
    }));

    const normalizer = (data) =>
      data.map((r) => ({
        id: r.id,
        title: r.title,
        owner: $tEmpName(r.owner),
        period: r.month
          ? `${String(r.month).padStart(2, '0')}/${r.fiscalYear}`
          : '—',
        status: $t(reportStatusKey(r.status, r.awaitingFinance)),
        statusTone: r.awaitingFinance ? 'AWAITING' : r.status,
        itemsCount: r.itemsCount,
        total: eur(r.totalEur),
        submittedAt: instant(r.submittedAt),
      }));

    const http = expensaApi('/reports');
    const {
      showPaginator,
      currentPage,
      total,
      pages,
      response,
      isLoading,
      execQuery,
    } = usePaginate(http, {
      normalizer,
      query: serializedFilters,
      toastNoRecords: false,
    });

    return {
      http,
      me,
      filters,
      showPaginator,
      currentPage,
      total,
      pages,
      items: response,
      isLoading,
      execQuery,
    };
  },
  data() {
    return {
      showNew: false,
      saving: false,
      draft: {title: '', description: '', employee: null},
      headers: [
        {
          name: 'title',
          title: this.$t('expensa.report_title'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 4},
        },
        {
          name: 'owner',
          title: this.$t('general.employee_name'),
          style: {flex: 3},
        },
        {
          name: 'period',
          title: this.$t('expensa.col_period'),
          style: {flex: 2},
        },
        {
          name: 'status',
          title: this.$t('general.status'),
          style: {flex: 3},
          cellRenderer: this.statusRenderer,
        },
        {
          name: 'itemsCount',
          title: this.$t('expensa.col_items'),
          style: {flex: 1},
        },
        {
          name: 'total',
          title: this.$t('expensa.col_total'),
          style: {flex: 2},
        },
        {
          name: 'submittedAt',
          title: this.$t('expensa.col_submitted'),
          style: {flex: 3},
        },
        {
          name: 'actions',
          slot: 'right',
          title: this.$t('general.actions'),
          style: {flex: 2},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            open: {
              onClick: this.onClickOpen,
              component: 'oxd-button',
              props: {
                label: this.$t('expensa.open'),
                displayType: 'text',
                size: 'medium',
              },
            },
          },
        },
      ],
      rules: {
        employee: [validSelection],
        q: [shouldNotExceedCharLength(100)],
        title: [required, shouldNotExceedCharLength(200)],
        description: [shouldNotExceedCharLength(1000)],
      },
    };
  },
  computed: {
    state() {
      return listState(this.isLoading, this.items, this.total);
    },
    showEmployeeFilter() {
      return !!this.me && (this.me.isFinance || this.me.isSupervisor);
    },
    statusOptions() {
      return REPORT_STATUSES.map((id) => ({
        id,
        label: this.$t(reportStatusKey(id, false)),
      }));
    },
    yearOptions() {
      const current = new Date().getFullYear();
      return Array.from({length: 7}, (_, i) => current + 1 - i).map((y) => ({
        id: y,
        label: String(y),
      }));
    },
    monthOptions() {
      return Array.from({length: 12}, (_, i) => ({
        id: i + 1,
        label: new Date(2000, i, 1).toLocaleString('it-IT', {month: 'long'}),
      }));
    },
  },
  beforeMount() {
    expensaApi('/me')
      .getAll()
      .then(({data}) => {
        this.me = data.data;
      })
      .catch(() => {
        // senza `me` il filtro dipendente resta nascosto: l'elenco funziona comunque
      });
  },
  methods: {
    statusRenderer(...[, , , row]) {
      return {
        component: markRaw(StatusPill),
        props: {label: row.status, tone: row.statusTone},
      };
    },
    async filterItems() {
      await this.execQuery();
    },
    onClickReset() {
      this.filters = {...defaultFilters};
      this.filterItems();
    },
    onClickOpen(item) {
      window.location.href = pageUrl('viewReport', item.id);
    },
    onClickNew() {
      this.draft = {title: '', description: '', employee: null};
      this.showNew = true;
    },
    onCancelNew() {
      this.showNew = false;
    },
    async onSaveNew() {
      this.saving = true;
      try {
        const body = {title: this.draft.title.trim()};
        const description = this.draft.description.trim();
        if (description) body.description = description;
        if (this.draft.employee) body.empNumber = this.draft.employee.id;
        const {data} = await expensaApi('/reports').create(body);
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_created'),
        });
        window.location.href = pageUrl('viewReport', data.data.id);
      } catch (e) {
        this.saving = false;
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-empty {
  padding: 0 1.2rem 1.2rem;
}
</style>
